// src/shared_new/modules/FormulationTable/FormulationTable.jsx
// Formulation Table Module - Uses PerlTable with formulation-specific logic

import React from "react";
import {
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Autocomplete,
  Box,
  Typography,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PerlTable from "../../components/PerlTable";

const pptColors = {
  background: "#191A1B",
  surface: "#242728",
  textPrimary: "#F1F2F3",
  textSecondary: "#798286",
  border: "rgba(241, 242, 243, 0.08)",
  white: "#FFFFFF",
};

/**
 * FormulationTable - Formulation/Recipe table module
 *
 * @param {Object} props
 * @param {Array} props.data - Array of formulation rows: [{ ingredient, percentage, functional_group }]
 * @param {Function} props.onRowChange - Callback: (index, field, value) => void
 * @param {Function} props.onRowAdd - Callback: () => void
 * @param {Function} props.onRowDelete - Callback: (index) => void
 * @param {Array} props.functionalGroups - List of functional group options
 * @param {String} props.brandColor - Brand color for styling
 * @param {Boolean} props.editable - Enable/disable editing (default: true)
 * @param {Boolean} props.showTotal - Show total percentage (default: true)
 */
export default function FormulationTable({
  data = [],
  onRowChange,
  onRowAdd,
  onRowDelete,
  functionalGroups = [],
  brandColor = "#ff6034",
  editable = true,
  showTotal = true,
  compositionTolerance = 10,
}) {
  // Calculate total percentage
  const totalPct = data.reduce(
    (acc, row) => acc + (Number.isFinite(Number(row.percentage)) ? Number(row.percentage) : 0),
    0
  );

  // Column definitions
  const columns = [
    { id: "ingredient", label: "Ingredient", align: "left" },
    { id: "percentage", label: "Composition (%)", width: "15%", align: "left" },
    { id: "functional_group", label: "Functional Group", align: "left" },
    { id: "actions", label: "", width: "60px", align: "center" },
  ];

  // Render each row
  const renderRow = (row, idx) => (
    <TableRow
      key={idx}
      sx={{
        "& td": {
          borderBottom: `1px solid ${pptColors.textSecondary}`,
        },
      }}
    >
      {/* Ingredient */}
      <TableCell>
        <TextField
          fullWidth
          size="small"
          value={row.ingredient || ""}
          inputProps={{ "aria-label": `Ingredient name: ${String(row.ingredient || "").trim() || `row ${idx + 1}`}` }}
          onChange={(e) => editable && onRowChange(idx, "ingredient", e.target.value)}
          disabled={!editable}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      </TableCell>

      {/* Percentage */}
      <TableCell>
        <TextField
          fullWidth
          size="small"
          type="number"
          value={row.percentage ?? ""}
          onChange={(e) => editable && onRowChange(idx, "percentage", e.target.value)}
          onBlur={(e) => {
            if (!editable) return;
            const val = parseFloat(e.target.value);
            if (!isNaN(val)) {
              onRowChange(idx, "percentage", val.toFixed(2));
            }
          }}
          disabled={!editable}
          inputProps={{
            "aria-label": `Composition percentage: ${String(row.ingredient || "").trim() || `row ${idx + 1}`}`,
            step: "0.01",
            min: "0",
            max: "100",
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
      </TableCell>

      {/* Functional Group */}
      <TableCell>
        <Autocomplete
          size="small"
          value={row.functional_group || null}
          onChange={(e, val) => editable && onRowChange(idx, "functional_group", val)}
          options={functionalGroups}
          disabled={!editable}
          renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, "aria-label": `Functional group: ${String(row.ingredient || "").trim() || `row ${idx + 1}`}` }} />}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          componentsProps={{
            paper: {
              sx: {
                bgcolor: pptColors.surface,
                "& .MuiAutocomplete-listbox": {
                  "& .MuiAutocomplete-option": {
                    color: pptColors.textPrimary,
                    "&:hover": {
                      bgcolor: alpha(pptColors.white, 0.08),
                    },
                    "&[aria-selected='true']": {
                      bgcolor: alpha(pptColors.white, 0.12),
                      "&:hover": {
                        bgcolor: alpha(pptColors.white, 0.16),
                      },
                    },
                  },
                },
                "& .MuiAutocomplete-noOptions": {
                  color: pptColors.textPrimary,
                },
              },
            },
          }}
        />
      </TableCell>

      {/* Delete Action */}
      <TableCell>
        {editable && (
          <IconButton aria-label={`Delete ingredient: ${String(row.ingredient || "").trim() || `row ${idx + 1}`}`} size="small" onClick={() => onRowDelete(idx)} sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );

  // Header actions (total + add button)
  const headerActions = (
    <>
      {showTotal && (
        <Typography
          variant="body2"
          sx={{
            color: pptColors.textSecondary,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          Total Composition (%): {totalPct.toFixed(2)}%{" "}
          {Math.abs(totalPct - 100) <= compositionTolerance ? (
            <span style={{ color: "#4caf50" }}>✓</span>
          ) : (
            <span style={{ color: "#ff6b6b" }}>⚠️</span>
          )}
        </Typography>
      )}
      {editable && (
        <IconButton
          aria-label="Add ingredient"
          onClick={onRowAdd}
          size="small"
          sx={{
            color: brandColor,
            "&:hover": { bgcolor: alpha(brandColor, 0.1) },
          }}
        >
          <AddCircleOutlineIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <PerlTable
      columns={columns}
      data={data}
      renderRow={renderRow}
      headerActions={headerActions}
      size="small"
    />
  );
}
